import { css } from "@linaria/core";
import cx from "classnames";
import React, { memo } from "react";
import { useNavigate } from "react-router";
import t from "../../../../../i18n";
import useToasts from "@ui/Toast/useToasts";
import Button from "@ui/Button";
import ButtonIcon from "@ui/ButtonIconV2";
import { AddCircleLine, SidebarFoldLine, SidebarUnfoldLine } from "@ui/IconV2/react";
import { colors, spacings } from "@ui/Theme";
import { useDashboardCreateCallback } from "../../hook/action/create";
import { useDashboardListingFolding } from "../../hook/useDashboardListingFolding";
import { DashboardList } from "./List";
import { printableHiddenInline } from "../../utils/print";
import { useUserCanEdit } from "../../hook/useUserCanEdit";
import { shadows } from "@ui";
import { useTranslations } from "../../../../../api/useTranslations";
import { toPath } from "../../../../../router/helpers/path";
import { useDashboards } from "../../hook/api/dashboard";
import { useDashboardEditorContext } from "../../hook/context/editor";
import { useLockDashboardCallback } from "../../hook/action/lock";

const dashboardAddButton = css`
  height: auto;
  justify-self: start;
`;

const asideFoldingToggle = css`
  /* somehow necessary in demo env (dunno) */
  width: max-content !important;
  height: max-content !important;
  justify-self: end !important;
`;

const dashboardListingContainer = css`
  background-color: ${colors.titanium50};
  height: calc(100vh - var(--primary-header-height));
  padding: 0 ${spacings.l} 0 ${spacings.l};
  width: var(--dashboard-listing-width);
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: ${shadows.shadow100};

  ${printableHiddenInline}

  &,
  .${dashboardAddButton}, .${asideFoldingToggle}, .entries {
    will-change: transform;
    transition: transform var(--section-column-transition-duration)
      var(--section-column-transition-ease);
  }

  .entries--1 {
    transition-duration: calc(var(--section-column-transition-duration) * 1.5);
  }

  .entries--2 {
    transition-duration: calc(var(--section-column-transition-duration) * 2);
  }

  &.folded {
    transform: translateX(calc(-100% + 50px));
    overflow: hidden;

    .${dashboardAddButton}, .entries {
      transform: translateX(-50px);
    }

    .${asideFoldingToggle} {
      transform: translateX(20px);
    }
  }

  header {
    /* background-color: ${colors.titanium50}; */
    display: grid;
    grid-auto-flow: row;
    gap: 4px;
    position: sticky;
    top: 0;
    /* margin-bottom: 8px; */
    padding-bottom: 24px;
    padding-top: 8px;
    background-image: linear-gradient(to top, transparent 0%, ${colors.titanium50} 30%);
    z-index: 1;
  }
`;

export const DashboardListing = memo(
  ({
    projectId,
    dashboardId,
    className,
  }: {
    projectId: string;
    dashboardId?: string;
    className?: string;
  }) => {
    const { dashboards } = useDashboards(projectId);
    const useCanEdit = useUserCanEdit(projectId);
    const { addToast } = useToasts();
    const navigate = useNavigate();
    const [isFolded, setIsFolded] = useDashboardListingFolding({});
    const translations = useTranslations();
    const editor = useDashboardEditorContext();

    /**
     * @note Empty string will still create a dashboard,
     * but will not emerge in the list as we index dashboards
     * by their projectId from the route.projectId parameter.
     */
    const createDashboardCallback = useDashboardCreateCallback(projectId);
    const lockDashboard = useLockDashboardCallback(projectId);
    const createDashboard = React.useCallback(() => {
      createDashboardCallback()
        ?.then(async result => {
          if (!result || !result.dashboard || !projectId || typeof result.dashboard.id !== "string")
            return;

          await lockDashboard(result.dashboard.id);
          await editor?.clear();
          await editor?.setup(result);
          addToast("success", t("project.dashboard.actions.create.success", translations));
          void navigate(
            toPath("/projects/:projectId/dashboards/:dashboardId/edit", {
              projectId,
              dashboardId: result.dashboard.id,
            }),
          );
        })
        .catch(() => addToast("error", t("project.dashboard.actions.create.error", translations)));
    }, [
      addToast,
      createDashboardCallback,
      navigate,
      projectId,
      translations,
      editor,
      lockDashboard,
    ]);

    const publishedDashboards = React.useMemo(
      () =>
        dashboards.filter(d => d.published).sort((a, b) => a.title.localeCompare(b.title)) ?? [],
      [dashboards],
    );
    const unpublishedDashboards = React.useMemo(
      () =>
        dashboards.filter(d => !d.published).sort((a, b) => a.title.localeCompare(b.title)) ?? [],
      [dashboards],
    );

    return (
      <aside className={cx(dashboardListingContainer, className, { folded: isFolded })}>
        <header>
          <ButtonIcon className={asideFoldingToggle} onClick={() => setIsFolded(s => !s)}>
            {isFolded && <SidebarUnfoldLine size="m" color={colors.carbon800} />}
            {!isFolded && <SidebarFoldLine size="m" color={colors.carbon800} />}
          </ButtonIcon>
          {useCanEdit && (
            <Button
              variant="link"
              leftIcon={<AddCircleLine />}
              className={dashboardAddButton}
              onClick={createDashboard}
            >
              {t("project.dashboard.actions.create", translations)}
            </Button>
          )}
        </header>
        <DashboardList
          dashboards={publishedDashboards}
          selectedDashboardId={dashboardId}
          placeholder={t("project.dashboards.listing.published.empty", translations)}
          title={t("project.dashboards.listing.published.title")}
          className="entries entries--1"
        />
        <DashboardList
          dashboards={unpublishedDashboards}
          selectedDashboardId={dashboardId}
          placeholder={t("project.dashboards.listing.unpublished.empty", translations)}
          title={t("project.dashboards.listing.unpublished.title")}
          className="entries entries--2"
        />
      </aside>
    );
  },
);
